import React from "react";
import { Typography } from "@mui/material";
import "react-medium-image-zoom/dist/styles.css";
import "../JaldiLab.css";
import {
  captionWithBorders,
  getTextContentHeading,
  getTextContentWithHeading,
  h1style,
  h2style,
  h3style,
  paraStyle,
  sectionStyle,
  subHeadingContent,
} from "./ChapterList";
import objective from "../../../assets/SvgIcons/obj.svg";
import user from "../../../assets/SvgIcons/users.svg";
import adjacencies from "../../../assets/SvgIcons/adjacencies.svg";
import designConsideration from "../../../assets/SvgIcons/design-consideration.svg";
import libraryImg from "../../../assets/Images/Library Images/Library.png";
import CaptionWithBorders1 from "../../../components/ChapterImage/Image";
import { FigureDesc64 } from "../../../utils/tmp/ImageDescription/LibraryID";

const Library = ({ goToSection }) => {
  const libraryAreaOject = {
    icon: <img src={objective} />,
    heading: "Objectives",
    data: [
      <>
        Provide a{" "}
        <span style={{ color: "#DC002D" }}>flexible and adaptable design </span>{" "}
        for the library, to serve the immediate and future needs of the Judges,
        Advocates, and administrative staff;
      </>,

      <>
        Adopt a{" "}
        <span style={{ color: "#DC002D" }}>
          system for efficient organisation{" "}
        </span>
        of journals and other materials, including incorporating a flexible
        digital system for streamlined organisation and retrieval of legal
        resources;
      </>,

      <>
        Ensure an environment where the{" "}
        <span style={{ color: "#DC002D" }}>
          books and materials are protected from humidity and other threats{" "}
        </span>{" "}
        that could damage them;{" "}
      </>,

      <>
        <span style={{ color: "#DC002D" }}>
          Ensure preservation of materials through digital archiving{" "}
        </span>{" "}
        while enabling remote access for collaboration beyond physical
        boundaries, and promotion of continuous professional development and
        collaborative research; and{" "}
      </>,

      <>
        Ensure that all spaces in the libraries are{" "}
        <span style={{ color: "#DC002D" }}>
          accessible for diverse user groups.
        </span>{" "}
      </>,
    ],
    isOrdered: true,
    isImage: true,
  };

  const usersObject = {
    icon: <img src={user} />,
    heading: "Users",
    data: [
      "Judges",
      "Advocates",
      "Librarians",
      "Staff maintaining the library resources",
    ],
    isOrdered: true,
    isImage: true,
  };

  const adjacenciesObject = {
    icon: <img src={adjacencies} />,
    heading: "Adjacencies",
    data: [
      "The libraries need not be part of the public facing functions of the Court Complex.",

      "Libraries for the Advocates and administrative staff must be accessible from the Advocates’ corridor and from the administrative wing.",

      "The Judges’ library must be directly accessible from the Judges’ corridor.",

      "The office of the Head Librarian/library staff must be in close proximity to the libraries.",

      "The libraries must be located close to lifts and elevators, for easy transporting of relevant materials to Courtrooms, Judges’ chambers, PP/GP offices, etc.",
    ],
    isOrdered: false,
    isImage: true,
  };

  const designConsiderationsObject = [
    {
      icon: <img src={designConsideration} />,
      heading: "Design Considerations",
      items: [
        "The libraries must be designed to ensure adequate space between the aisles for the movement of diverse user groups, including Persons with Disabilities. ",
        "Adequate space must also be provided for manoeuvring the trolleys used for transporting books from the library to the Courtrooms/Judges’ chambers/PP and GP offices, etc. ",

        "All libraries must be equipped with accessible ergonomic furniture, including modular seating, conference tables, etc. ",

        "Libraries must be equipped with information kiosks and digital case display systems, highlighting ongoing cases in all Courtrooms. ",

        "A part of the library must be designed for the users to have digital access to study material, and must be equipped with adequate technological equipment including all necessary software to facilitate the same and ensure privacy. ",

        "Digitisation of the available material as well as additional online subscriptions must be provided to ensure remote access as well as reduction of storage space. A supporting digital catalogue system must be incorporated to keep track of the digitised materials and to ensure easy integration with the physical library.",

        "Computer systems must be provided with adequate internet and intranet connections to support a digital catalogue system/electronic library. ",

        "Wherever possible, Mobile Compactor Storage Systems with rolling shelves must be used to help save space in the library and to make retrieving of books and journals easier. Adequate space must also be apportioned to account for modular storage solutions for the digital resources being provided.",

        "Modular storage shelves of uniform height must be used, with some shelves being lower to ensure sightlines across the library. ",

        "Scanners and photocopying facilities and other reprographic services must be provided within the library. ",

        "If the library has stacked shelves, portable ladders/steps/stools must be provided to access books at the top shelves. ",

        "The positioning of windows must be considered relative to the placement of the storage facilities when designing the library, to ensure that the room receives adequate ventilation and natural light. Depending on the geographical location of the library, suitable methods to naturally regulate the internal temperature of the rooms must be adopted.",
      ],
    },
    {
      heading: "Reading Room",
      items: [
        "Specific space must be provided for a reading room within the libraries.",

        "The reading room must be provided with adequate ergonomic seating and reading tables. Stand-up reading areas and counters must also be provided.  ",

        "It must have natural lighting and ventilation.",

        "Adequate space and technological support must be provided for accessing digital facilities.",
      ],
    },
    {
      heading: "Space for Library Staff and Head Librarian",
      items: [
        "Adequate working space must be provided for the Head Librarian as well as the staff working in the library.",
        <>
          The Head Librarian must have their own office room wherever possible.
          For details of a single person office space, please refer to{" "}
          <span
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesSingleWorkspace")
            }
            style={sectionStyle}
          >
            Section 2.5.B.
          </span>{" "}
        </>,

        "The office room must have adequate workspace, along with seating for visitors. ",

        "The room must be provided with adequate storage space to store files and books as well as their personal belongings.",

        <>
          The space provided for the Library Staff must be accessible with
          proper ventilation and ergonomic seating. For further details of a
          multi-person office space, please refer to{" "}
          <span
            onClick={() =>
              goToSection("officeSpaces", "officeSpacesMultiPersonWorkspace")
            }
            style={sectionStyle}
          >
            Section 2.5.C.
          </span>
        </>,

        "The work space for Library Staff must have adequate storage facilities as well as provision for a scanner/copier for the use of the staff. ",

        "Adequate storage space must be provided for the personal belongings of the staff members.",
      ],
    },
  ];

  return (
    <div>
      <Typography>
        <h1 id="handbookPrinciple" style={h1style}>
          PART C - THE HANDBOOK
        </h1>{" "}
        <h2 id="HeadingChapterTwo" style={h2style}>
          <span style={{ fontSize: "24px" }}>2. </span>
          ORGANIZATION AND DESIGN OF SPACES OF THE COURT COMPLEX
        </h2>
        <h3 id="leagalFacilities" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.9. </span>
          Legal Facilities
        </h3>
        <h3 id="headingLibrary" style={h3style}>
          <span style={{ fontSize: "20px" }}>2.9.</span>C. Library
        </h3>
        <p style={paraStyle}>
          The Court Complex must be equipped with library facilities for all
          users. Specific libraries for judges and Advocates are necessary to
          ensure that they have access to the requisite resources, for
          continuous learning and updates regarding new developments in legal
          jurisprudence. Libraries must also be provided to the administrative
          staff to ensure that they have access to resources relevant to
          executing their specific functions. The libraries for the judicial
          officers and the staff may be combined, if required, depending upon
          the number of staff at that particular Court.
        </p>
        <p style={paraStyle}>
          Additionally, specific facilities must be provided for the offices of
          the Public Prosecutors and the Government Pleaders (
          <span
            onClick={() =>
              goToSection("advocatesBarAssociation", "ChambersData")
            }
            style={sectionStyle}
          >
            Section 2.6.B.
          </span>
          ). Relevant online subscription services and databases must also be
          incorporated as part of the digital resources for the library.
        </p>
        {getTextContentHeading(libraryAreaOject)}
        {getTextContentHeading(usersObject)}
        {getTextContentHeading(adjacenciesObject)}
        {subHeadingContent(designConsiderationsObject)}
        <CaptionWithBorders1
          image={[libraryImg]}
          isZoom={true}
          caption={"Figure 64: Typical floor plan of a library"}
          zoomImgStyle={{ height: "500px" }}
          imageDescription={FigureDesc64}
        />
      </Typography>
    </div>
  );
};

export default Library;
